import React, { useState, useRef, useEffect } from 'react';

import styled from 'styled-components';
import Lottie from 'lottie-react';

import LoadingAnimation from '../../../Animation/NewLoading.json';
import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import LockerGrid from '../../../Component/LockerComponent/LockerGrid';
import LockerEditView from './LockerEditView';

import { ReactComponent as EmptySignSvg } from '../../../image/EmptySign.svg';
import { ReactComponent as EditSvg } from '../../../image/Edit.svg';

function LockerDefaultView(props) {
  const {
    totalLockers,
    isClickedLockerDivisionId,
    LockerRef,
    isSaveLoading,
    onSaveStart,
    onSaveLockerChangesFn,
    onSaveDivisionNameFn,
    onClickUsageHistoryFn,
    onClickLockerActionFn,
    onClickChangeStateFn,
    onClickDeleteLockerFn,
    onClickChangeNameFn,
    onClickSendMessageFn,
    onClickExtendFn,
    onClickModifyFn,
    onDeleteLockersFn,
    onEditModeChange,
    onChangeDivisionNameDraft,
    onClearDivisionNameDraft
  } = props;
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();
  const [editModeMap, setEditModeMap] = useState({});
  const [savingDivisionId, setSavingDivisionId] = useState(null);
  const [divisionNameMap, setDivisionNameMap] = useState({});
  const [stagedDeletions, setStagedDeletions] = useState({});
  const lockChangesRef = useRef({});
  const nameChangesRef = useRef({});
  const addedLockersRef = useRef({});
  const swapChangesRef = useRef({});

  // 저장 로딩 상태 초기화 //
  useEffect(() => {
    if (!isSaveLoading) setSavingDivisionId(null);
  }, [isSaveLoading]);

  // 구분 전환시 수정 상태 초기화 //
  useEffect(() => {
    setEditModeMap({});
    setDivisionNameMap({});
    setStagedDeletions({});
    lockChangesRef.current = {};
    nameChangesRef.current = {};
    addedLockersRef.current = {};
    swapChangesRef.current = {};
  }, [isClickedLockerDivisionId]);

  // 편집 모드 변경 부모에 전달 //
  useEffect(() => {
    const isAnyEditMode = Object.values(editModeMap).some(v => v === true);

    onEditModeChange?.(isAnyEditMode);
  }, [editModeMap, onEditModeChange]);

  const onSwapChange = (divisionIndex, newSwapMap) => {
    swapChangesRef.current[divisionIndex] = newSwapMap;
  };

  // 락커 삭제 staging — 저장하기 클릭 전까지는 API 호출하지 않음 //
  const onDeleteLockers = (divisionIndex, lockerIds) => {
    lockChangesRef.current[divisionIndex] = {};
    nameChangesRef.current[divisionIndex] = {};
    addedLockersRef.current[divisionIndex] = null;
    swapChangesRef.current[divisionIndex] = {};
    setStagedDeletions(prev => {
      const prevIds = prev[divisionIndex] || [];
      const merged = Array.from(new Set([...prevIds, ...lockerIds]));
      return { ...prev, [divisionIndex]: merged };
    });
  };

  const onChangeLock = (divisionIndex, lockerIndex, hasChanged, newIsLocked) => {
    if (!lockChangesRef.current[divisionIndex]) lockChangesRef.current[divisionIndex] = {};
    if (hasChanged) lockChangesRef.current[divisionIndex][lockerIndex] = newIsLocked;
    else delete lockChangesRef.current[divisionIndex][lockerIndex];
  };

  const onChangeName = (divisionIndex, lockerIndex, hasChanged, newName) => {
    if (!nameChangesRef.current[divisionIndex]) nameChangesRef.current[divisionIndex] = {};
    if (hasChanged) nameChangesRef.current[divisionIndex][lockerIndex] = newName;
    else delete nameChangesRef.current[divisionIndex][lockerIndex];
  };

  function renderLockerGrid(lockerData, divisionIndex) {
    return (
      <LockerGrid
        lockerData={lockerData}
        divisionIndex={divisionIndex}
        LockerRef={LockerRef}
        onClickUsageHistoryFn={onClickUsageHistoryFn}
        onClickLockerActionFn={onClickLockerActionFn}
        onClickChangeStateFn={onClickChangeStateFn}
        onClickDeleteLockerFn={onClickDeleteLockerFn}
        onClickChangeNameFn={onClickChangeNameFn}
        onClickSendMessageFn={onClickSendMessageFn}
        onClickExtendFn={onClickExtendFn}
        onClickModifyFn={onClickModifyFn} />
    );
  }

  function onChangeDivisionNameInput(id, e) {
    const newName = e.target.value;
    setDivisionNameMap(prev => ({ ...prev, [id]: newName }));
    onChangeDivisionNameDraft?.(id, newName);
  }

  const onClickToggleEditMode = (id, index, currentDataName) => {
    if (editModeMap[id]) {
      const lockChanged = lockChangesRef.current[index] || {};
      const nameChanged = nameChangesRef.current[index] || {};
      const addedInfo = addedLockersRef.current[index] || null;
      const addedPositions = addedInfo ? (addedInfo.positions ?? []) : [];
      const baseCount = addedInfo ? (addedInfo.baseCount ?? 0) : 0;
      const coordChanges = swapChangesRef.current[index] || {};
      const hasCoordChanges = Object.keys(coordChanges).length > 0;
      const deletedIds = stagedDeletions[index] || [];

      const newDivisionName = divisionNameMap[id] ?? currentDataName;
      const divisionNameChanged = newDivisionName !== currentDataName;

      if (divisionNameChanged && newDivisionName.trim() === '') {
        alert(t('main.locker.divisionNameDuplicateOrEmpty'));
        return;
      }

      const newLockers = [];
      let existingNameChanges = nameChanged;
      if (addedPositions.length > 0) {
        const divisionId = id > 0 ? id : null;
        // 기존 visible 락커 이름 + 같은 batch 내 이미 결정된 신규 이름까지 모아서 중복 방지
        const stagedDeletedSet = new Set(deletedIds);
        const usedNames = new Set(
          (totalLockers[index]?.lockers ?? [])
            .filter(l => !stagedDeletedSet.has(l.id))
            .map(l => existingNameChanges[
              (totalLockers[index].lockers).indexOf(l)
            ] ?? l.lockerName)
            .filter(Boolean)
        );
        // 자동 생성 이름: "${divisionName} ${A}-${num}" + 중복 시 (N) 접미사 //
        const buildUniqueAutoName = (x, y) => {
          const base = `${newDivisionName} ${String.fromCharCode(65 + x)}-${y + 1}`;
          if (!usedNames.has(base)) return base;
          let suffix = 2;
          while (usedNames.has(`${base}(${suffix})`)) suffix++;
          return `${base}(${suffix})`;
        };
        for (const pos of addedPositions) {
          const typedName = pos.lockerIdx in nameChanged ? nameChanged[pos.lockerIdx] : null;
          // 자동 이름의 좌표 suffix는 최초 좌표(initialX/Y) 기반 → swap 후에도 이름 고정 //
          const nameX = pos.initialX ?? pos.x;
          const nameY = pos.initialY ?? pos.y;
          const name = typedName !== null ? typedName : buildUniqueAutoName(nameX, nameY);

          usedNames.add(name);
          newLockers.push({
            name,
            lockerDivisionId: divisionId,
            x: pos.x,
            y: pos.y,
            // 신규 락커의 lock 상태 — 저장 후 별도 PATCH로 Invalid 적용에 사용 //
            isLocked: lockChanged[pos.lockerIdx] === true
          });
        }
        existingNameChanges = Object.fromEntries(
          Object.entries(nameChanged).filter(([k]) => Number(k) < baseCount)
        );
      }

      const hasEmptyLockerName =
        Object.values(existingNameChanges).some(n => !n || n.trim() === '') ||
        newLockers.some(l => !l.name || l.name.trim() === '');
      if (hasEmptyLockerName) {
        alert(t('main.locker.enterLockerName'));
        return;
      }

      const hasLockerChanges = Object.keys(lockChanged).length > 0 || Object.keys(existingNameChanges).length > 0;
      const hasDeletions = deletedIds.length > 0;

      // 좌표 없는 락커들 — 어떤 편집이든 발생 시 visible 좌표를 백엔드에 영구화 //
      const FALLBACK_COLS = 7;
      const lockersInDivision = totalLockers[index]?.lockers ?? [];
      const dupSeen = new Set();
      const hasDupCoords = lockersInDivision.some(l => {
        const key = `${l.x ?? 0},${l.y ?? 0}`;
        if (dupSeen.has(key)) return true;
        dupSeen.add(key);
        return false;
      });
      const hasAnyEdit = hasDeletions
        || hasLockerChanges
        || addedPositions.length > 0
        || hasCoordChanges
        || divisionNameChanged;
      if (hasDupCoords && hasAnyEdit) {
        const excludedSet = new Set(deletedIds);
        const visiblePositions = [];
        lockersInDivision.forEach((l, i) => {
          if (excludedSet.has(l.id)) return;
          const dragged = coordChanges[l.id];
          const defaultPos = { x: i % FALLBACK_COLS, y: Math.floor(i / FALLBACK_COLS) };
          const origPos = dragged || defaultPos;
          visiblePositions.push({ id: l.id, origX: origPos.x, origY: origPos.y });
        });
        const byRow = new Map();
        visiblePositions.forEach(p => {
          if (!byRow.has(p.origY)) byRow.set(p.origY, []);
          byRow.get(p.origY).push(p);
        });
        const sortedYs = Array.from(byRow.keys()).sort((a, b) => a - b);
        sortedYs.forEach((origY, newY) => {
          const list = byRow.get(origY).sort((a, b) => a.origX - b.origX);
          list.forEach((p, newX) => {
            coordChanges[p.id] = { x: newX, y: newY };
          });
        });
      }
      const hasCoordChangesFinal = Object.keys(coordChanges).length > 0;

      if (hasLockerChanges || addedPositions.length > 0 || divisionNameChanged || hasCoordChangesFinal || hasDeletions) {
        setSavingDivisionId(id);
        onSaveStart?.();
      }

      if (hasLockerChanges || addedPositions.length > 0 || hasCoordChangesFinal || hasDeletions) {
        onSaveLockerChangesFn?.(index, lockChanged, existingNameChanges, newLockers, coordChanges, deletedIds);
      }

      if (divisionNameChanged) {
        onSaveDivisionNameFn?.(id, newDivisionName);
      }

      lockChangesRef.current[index] = {};
      nameChangesRef.current[index] = {};
      addedLockersRef.current[index] = null;
      swapChangesRef.current[index] = {};
      setStagedDeletions(prev => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      onClearDivisionNameDraft?.(id);
    }
    else {
      setDivisionNameMap(prev => ({ ...prev, [id]: currentDataName }));
    }

    setEditModeMap(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <DivisionInfoBox>
      {
        totalLockers.map((data, index) => (
          data.id !== 0 && !(data.id === -1 && data.lockers.length === 0) && (isClickedLockerDivisionId === 0 || data.id === isClickedLockerDivisionId) &&
          <DivisionItemWrapper key={'TotalLocker-' + index}>
            <ViewBox $isDark={isDark} $isSaving={savingDivisionId === data.id}>
              {savingDivisionId === data.id ? (
                <SaveOverlay>
                  <SaveLottieWrapper>
                    <Lottie animationData={LoadingAnimation} loop />
                  </SaveLottieWrapper>
                </SaveOverlay>
              ) : (
                <ViewContentWrapper>
                  <EditButton isEditMode={!!editModeMap[data.id]} onClick={() => onClickToggleEditMode(data.id, index, data.name)}>
                    <EditIcon $isEditMode={!!editModeMap[data.id]} />
                    {editModeMap[data.id] ? '저장하기' : '수정하기'}
                  </EditButton>
                  <DivisionNameBoxWrapper>
                    {
                      editModeMap[data.id] ?
                        <DivisionNameInputWrapper>
                          <DivisionNameInput
                            value={divisionNameMap[data.id] ?? data.name}
                            onChange={(e) => onChangeDivisionNameInput(data.id, e)}
                          />
                        </DivisionNameInputWrapper>
                        :
                        <DivisionNameBox>
                          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subBackground} hover='default'>{data.name}</Text>
                        </DivisionNameBox>
                    }
                  </DivisionNameBoxWrapper>
                  {
                    editModeMap[data.id] ?
                      <LockerEditView
                        data={data}
                        divisionIndex={index}
                        divisionNameDraft={divisionNameMap[data.id] ?? data.name}
                        LockerRef={LockerRef}
                        excludedLockerIds={stagedDeletions[index] || []}
                        onLockChange={(lockerIndex, hasChanged, newIsLocked) => onChangeLock(index, lockerIndex, hasChanged, newIsLocked)}
                        onNameChange={(lockerIndex, hasChanged, newName) => onChangeName(index, lockerIndex, hasChanged, newName)}
                        onAddLockersChange={(divisionIndex, count) => { addedLockersRef.current[divisionIndex] = count; }}
                        onDeleteLockersFn={onDeleteLockers}
                        onClickUsageHistoryFn={onClickUsageHistoryFn}
                        onClickLockerActionFn={onClickLockerActionFn}
                        onClickChangeStateFn={onClickChangeStateFn}
                        onClickDeleteLockerFn={onClickDeleteLockerFn}
                        onClickChangeNameFn={onClickChangeNameFn}
                        onClickSendMessageFn={onClickSendMessageFn}
                        onClickExtendFn={onClickExtendFn}
                        onSwapChange={onSwapChange} />
                      :
                      data.lockers.length === 0 ?
                        <NoneBox>
                          <EmptyImage />
                          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText} hover='default'>{t('main.locker.noLocker')}</Text>
                        </NoneBox>
                        :
                        renderLockerGrid(data, index)
                  }
                </ViewContentWrapper>
              )}
            </ViewBox>
            {(isClickedLockerDivisionId === 0 && index !== 0 && totalLockers.length - 1 !== index) && <DivisionLine />}
          </DivisionItemWrapper>
        ))
      }
    </DivisionInfoBox>
  );
}

export default LockerDefaultView;

const DivisionInfoBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
`;

const DivisionItemWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;
`;

const ViewBox = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  padding: 20px;
  box-sizing: border-box;
  width: 100%;
  ${props => props.$isSaving ? 'height: 400px;' : ''}

  border-radius: 20px;
  background-color: ${props => props.theme.subBackground};
  box-shadow: ${props => props.$isDark ? 'none' : '0px 40px 50px rgba(240, 232, 227, 0.5)'};
`;

const DivisionNameBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  margin-bottom: 15px;

  width: 100%;
`;

const EditButton = styled.div`
  position: absolute;
  top: 0px;
  right: 0px;

  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 5px;
  padding: 0 15px;

  height: 30px;

  border-radius: 5px;
  background-color: ${props => props.isEditMode ? props.theme.primary : props.theme.buttonFill};

  font-family: NotoSansKR-Medium;
  font-size: 13px;
  color: ${props => props.isEditMode ? props.theme.subBackground : props.theme.text};

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const EditIcon = styled(EditSvg)`
  flex-shrink: 0;
  width: 14px;
  height: 14px;

  & path {
    fill: ${props => props.$isEditMode ? props.theme.subBackground : props.theme.text};
  }
`;

const DivisionNameBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 5px 20px;

  border-radius: 5px;
  background-color: ${props => props.theme.buttonFill2};
`;

const NoneBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  gap: 10px;
  padding: 40px 0px;

  width: 100%;
`;

const EmptyImage = styled(EmptySignSvg)`
  width: 148px;
  height: 62px;

  /* 배경 표지판 (root level rect) */
  & > rect {
    fill: ${props => props.theme.buttonFill3};
  }

  /* "EMPTY" 텍스트 */
  & > path {
    fill: ${props => props.theme.white};
  }

  /* 매다는 끈 2개 (g 안의 rects) */
  & > g rect {
    fill: ${props => props.theme.line};
  }
`;

const DivisionNameInputWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 0px 20px;

  width: fit-content;
  max-width: 90%;
  height: 31px;

  box-sizing: border-box;

  background-color: ${props => props.theme.subBackground};
  background-image: ${props => {
    const color = encodeURIComponent(props.theme.line);
    return `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='5' ry='5' stroke='${color}' stroke-width='1' stroke-dasharray='8%2c 12' stroke-linecap='square'/%3e%3c/svg%3e")`;
  }};
  border-radius: 5px;
`;

const DivisionNameInput = styled.input`
  field-sizing: content;

  min-width: 5px;
  max-width: 100%;

  font-family: NotoSansKR-Medium;
  font-size: 14px;
  color: ${props => props.theme.text};
  text-align: center;

  background: transparent;
  border: none;
  outline: none;
  padding: 0;
  margin: 0;
`;

const DivisionLine = styled.div`
  margin: 30px 0px;
  
  width: calc(100% - 10px);
  height: 1px;

  border-radius: 2px;
  background-color: ${props => props.theme.line};
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  &:hover {
    cursor: ${props => props.hover};
  }

  transition: all 0.3s ease-in-out;
`;

const SaveOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;

  display: flex;
  justify-content: center;
  align-items: center;

  border-radius: inherit;
  background-color: ${props => props.theme.subBackground};
`;

const SaveLottieWrapper = styled.div`
  width: 160px;
  height: 160px;
`;

const ViewContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
`;
